import React from "react";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import logo from "@mapmycustomers/ui/assets/logo.svg";

import styles from "./AuthLayout.module.scss";

// A chunk that fails to load hides the animation; the login app has no error boundary to catch it.
export const loadMapActions = () => import("../MapActions").catch(() => ({ default: () => null }));

const MapActions = React.lazy<React.ComponentType>(loadMapActions);

const messages = defineMessages({
  accountCount: {
    id: "auth.layout.decoration.accountCount",
    defaultMessage: "12 accounts",
    description: "Decorative pill label showing account count on the auth page background",
  },
  growth: {
    id: "auth.layout.decoration.growth",
    defaultMessage: "+32% growth",
    description: "Decorative pill label showing growth percentage on the auth page background",
  },
  territory: {
    id: "auth.layout.decoration.territory",
    defaultMessage: "Territory West",
    description: "Decorative pill label showing territory name on the auth page background",
  },
});

export interface AuthLayoutProps {
  children: React.ReactNode;
  className?: string;
  mapActions?: boolean;
}

const AuthLayout = ({ children, className, mapActions }: AuthLayoutProps) => {
  const intl = useIntl();

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.gridOverlay} />
      {mapActions ? (
        <React.Suspense
          // The fallback must be null, and this boundary must be here: PublicRoute
          // wraps the whole page in a Suspense whose fallback is a full-screen
          // spinner, so without this local boundary the chunk load would blank the
          // sign-in form.
          fallback={null}
        >
          <MapActions />
        </React.Suspense>
      ) : null}

      <div className={styles.mobileOnly}>
        <div className={cn(styles.mobilePin, styles.mp1, styles.mobileCompletes)} />
        <div className={cn(styles.mobilePin, styles.mp2)} />
        <div className={cn(styles.mobilePin, styles.mp3)} />
        <div className={cn(styles.mobilePin, styles.mp4, styles.mobileCompletes)} />
        <div className={cn(styles.mobilePin, styles.mp5)} />
        <div className={cn(styles.mobilePin, styles.mp6, styles.mobileCompletes)} />
      </div>

      <div className={styles.desktopOnly}>
        <div className={cn(styles.heatBlob, styles.h1)} />
        <div className={cn(styles.heatBlob, styles.h2)} />
        <div className={cn(styles.heatBlob, styles.h3)} />
        <div className={cn(styles.heatBlob, styles.h4)} />

        <div className={cn(styles.mapPin, styles.pin1, styles.completes)} />
        <div className={cn(styles.mapPin, styles.pin2, styles.completes)} />
        <div className={cn(styles.mapPin, styles.pin3)} />
        <div className={cn(styles.mapPin, styles.pin4)} />
        <div className={cn(styles.mapPin, styles.pin5, styles.completes)} />
        <div className={cn(styles.mapPin, styles.pin6)} />
        <div className={cn(styles.mapPin, styles.pin7)} />
        <div className={cn(styles.mapPin, styles.pin8)} />

        <div aria-hidden className={cn(styles.pillLabel, styles.label1)}>
          {intl.formatMessage(messages.territory)}
        </div>
        <div aria-hidden className={cn(styles.pillLabel, styles.label2)}>
          {intl.formatMessage(messages.growth)}
        </div>
        <div aria-hidden className={cn(styles.pillLabel, styles.label3)}>
          {intl.formatMessage(messages.accountCount)}
        </div>
      </div>

      <main className={styles.main}>
        <img alt="MapMyCustomers logo" className={styles.logo} src={logo} />
        {children}
      </main>
    </div>
  );
};

export default AuthLayout;
